import { clientEnvModule } from "@/modules/client-env";

const MARKETING_ATTRIBUTION_COOKIE_NAME = "mem_marketing_attribution";
const MARKETING_ATTRIBUTION_LOCAL_STORAGE_KEY = "mem_marketing_attribution";
const MARKETING_ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_BROWSER_CLOCK_SKEW_MS = 5 * 60 * 1000;
const MAX_ATTRIBUTION_VALUE_LENGTH = 512;
const MAX_UTM_PARAM_COUNT = 20;
const MAX_UTM_PARAM_KEY_LENGTH = 64;

const MarketingAttributionQueryParam = {
  CAPTURED_AT: "marketing_attribution_captured_at",
  FBC: "fbc",
  FBCLID: "fbclid",
  FBP: "fbp",
  JOURNEY_ID: "marketing_journey_id",
  LANDING_PATH: "marketing_landing_path",
  REFERRER_DOMAIN: "marketing_referrer_domain",
  SEGMENT_ANONYMOUS_ID: "segment_anonymous_id",
} as const;

export interface MarketingAttribution {
  capturedAt?: string;
  fbc?: string;
  fbclid?: string;
  fbp?: string;
  journeyId?: string;
  landingPath?: string;
  referrerDomain?: string;
  segmentAnonymousId?: string;
  utmParams: Record<string, string>;
}

let capturedMarketingAttribution: MarketingAttribution | null = null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const safeAttributionValue = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return undefined;
  }

  return trimmed.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH);
};

const safeUtmParams = (value: unknown): Record<string, string> => {
  if (!isRecord(value)) {
    return {};
  }

  const params: Record<string, string> = {};
  for (const [key, rawValue] of Object.entries(value)) {
    if (
      !key.startsWith("utm_") ||
      key.length > MAX_UTM_PARAM_KEY_LENGTH ||
      Object.keys(params).length >= MAX_UTM_PARAM_COUNT
    ) {
      continue;
    }

    const safeValue = safeAttributionValue(rawValue);
    if (safeValue !== undefined) {
      params[key] = safeValue;
    }
  }
  return params;
};

const parseMarketingAttribution = (
  value: string | null | undefined
): MarketingAttribution | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const parseCandidates = [value];
  try {
    const decoded = decodeURIComponent(value);
    if (decoded !== value) {
      parseCandidates.push(decoded);
    }
  } catch {
    // Raw local-storage JSON may contain an unescaped percent sign.
  }

  for (const candidate of parseCandidates) {
    try {
      const parsed: unknown = JSON.parse(candidate);
      if (!isRecord(parsed)) {
        continue;
      }

      return {
        capturedAt: safeAttributionValue(parsed.capturedAt),
        fbc: safeAttributionValue(parsed.fbc),
        fbclid: safeAttributionValue(parsed.fbclid),
        fbp: safeAttributionValue(parsed.fbp),
        journeyId: safeAttributionValue(parsed.journeyId),
        landingPath: safeAttributionValue(parsed.landingPath),
        referrerDomain: safeAttributionValue(parsed.referrerDomain),
        segmentAnonymousId: safeAttributionValue(parsed.segmentAnonymousId),
        utmParams: safeUtmParams(parsed.utmParams),
      };
    } catch {
      // Try the encoded cookie representation after raw local-storage JSON.
    }
  }
  return null;
};

const readRawBrowserCookie = (name: string): string | undefined => {
  try {
    const prefix = `${name}=`;
    for (const part of document.cookie.split(";")) {
      const trimmed = part.trim();
      if (trimmed.startsWith(prefix)) {
        return trimmed.slice(prefix.length);
      }
    }
  } catch {
    // Cookies are unavailable in some desktop and privacy-restricted contexts.
  }
  return undefined;
};

export const readBrowserCookie = (name: string): string | undefined =>
  safeAttributionValue(readRawBrowserCookie(name));

const readSharedAttributionCookie = (): MarketingAttribution | null =>
  parseMarketingAttribution(readRawBrowserCookie(MARKETING_ATTRIBUTION_COOKIE_NAME));

const isExpiredAttribution = (attribution: MarketingAttribution, now = Date.now()): boolean => {
  if (attribution.capturedAt === undefined) {
    return true;
  }

  const capturedAt = Date.parse(attribution.capturedAt);
  const age = now - capturedAt;
  return (
    !Number.isFinite(capturedAt) ||
    age < -MAX_BROWSER_CLOCK_SKEW_MS ||
    age > MARKETING_ATTRIBUTION_TTL_MS
  );
};

const readLocalAttribution = (): MarketingAttribution | null => {
  try {
    const attribution = parseMarketingAttribution(
      localStorage.getItem(MARKETING_ATTRIBUTION_LOCAL_STORAGE_KEY)
    );
    if (attribution === null || !isExpiredAttribution(attribution)) {
      return attribution;
    }

    localStorage.removeItem(MARKETING_ATTRIBUTION_LOCAL_STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
};

const capturedAtMs = (attribution: MarketingAttribution | null): number => {
  if (attribution?.capturedAt === undefined) {
    return Number.NEGATIVE_INFINITY;
  }

  const timestamp = Date.parse(attribution.capturedAt);
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

const latestPersistedAttribution = (): MarketingAttribution | null => {
  const parsedCookieAttribution = readSharedAttributionCookie();
  const cookieAttribution =
    parsedCookieAttribution !== null && !isExpiredAttribution(parsedCookieAttribution)
      ? parsedCookieAttribution
      : null;
  const localAttribution = readLocalAttribution();

  if (
    cookieAttribution?.journeyId !== undefined &&
    cookieAttribution.journeyId === localAttribution?.journeyId
  ) {
    return mergeAttribution({
      incoming: cookieAttribution,
      persisted: localAttribution,
    });
  }

  return capturedAtMs(cookieAttribution) > capturedAtMs(localAttribution)
    ? cookieAttribution
    : (localAttribution ?? cookieAttribution);
};

const removeLocalAttribution = (): void => {
  try {
    localStorage.removeItem(MARKETING_ATTRIBUTION_LOCAL_STORAGE_KEY);
  } catch {
    // Local storage can be unavailable in privacy-restricted contexts.
  }
};

const extractUtmParams = (searchParams: URLSearchParams): Record<string, string> => {
  const params: Record<string, string> = {};
  for (const [key, value] of searchParams.entries()) {
    if (
      !key.startsWith("utm_") ||
      key.length > MAX_UTM_PARAM_KEY_LENGTH ||
      Object.keys(params).length >= MAX_UTM_PARAM_COUNT
    ) {
      continue;
    }

    const safeValue = safeAttributionValue(value);
    if (safeValue !== undefined) {
      params[key] = safeValue;
    }
  }
  return params;
};

const isMarketingAttributionQueryParam = (key: string): boolean =>
  key.startsWith("utm_") ||
  Object.values(MarketingAttributionQueryParam).some(param => param === key);

const extractQueryAttribution = (searchParams: URLSearchParams): MarketingAttribution | null => {
  const hasAttribution = Array.from(searchParams.keys()).some(isMarketingAttributionQueryParam);
  if (!hasAttribution) {
    return null;
  }

  return {
    capturedAt: safeAttributionValue(searchParams.get(MarketingAttributionQueryParam.CAPTURED_AT)),
    fbc: safeAttributionValue(searchParams.get(MarketingAttributionQueryParam.FBC)),
    fbclid: safeAttributionValue(searchParams.get(MarketingAttributionQueryParam.FBCLID)),
    fbp: safeAttributionValue(searchParams.get(MarketingAttributionQueryParam.FBP)),
    journeyId: safeAttributionValue(searchParams.get(MarketingAttributionQueryParam.JOURNEY_ID)),
    landingPath: safeAttributionValue(
      searchParams.get(MarketingAttributionQueryParam.LANDING_PATH)
    ),
    referrerDomain: safeAttributionValue(
      searchParams.get(MarketingAttributionQueryParam.REFERRER_DOMAIN)
    ),
    segmentAnonymousId: safeAttributionValue(
      searchParams.get(MarketingAttributionQueryParam.SEGMENT_ANONYMOUS_ID)
    ),
    utmParams: extractUtmParams(searchParams),
  };
};

const mergeAttribution = ({
  incoming,
  persisted,
}: {
  incoming: MarketingAttribution | null;
  persisted: MarketingAttribution | null;
}): MarketingAttribution | null => {
  if (incoming === null) {
    return persisted;
  }
  if (persisted === null) {
    return incoming;
  }

  const isSameJourney =
    incoming.journeyId !== undefined && incoming.journeyId === persisted.journeyId;
  const isIdentityAugmentation = incoming.capturedAt === undefined;
  if (!isSameJourney && !isIdentityAugmentation) {
    return capturedAtMs(incoming) >= capturedAtMs(persisted) ? incoming : persisted;
  }

  return {
    capturedAt: incoming.capturedAt ?? persisted.capturedAt,
    fbc: incoming.fbc ?? persisted.fbc,
    fbclid: incoming.fbclid ?? persisted.fbclid,
    fbp: incoming.fbp ?? persisted.fbp,
    journeyId: incoming.journeyId ?? persisted.journeyId,
    landingPath: incoming.landingPath ?? persisted.landingPath,
    referrerDomain: incoming.referrerDomain ?? persisted.referrerDomain,
    segmentAnonymousId: incoming.segmentAnonymousId ?? persisted.segmentAnonymousId,
    utmParams: {
      ...persisted.utmParams,
      ...incoming.utmParams,
    },
  };
};

const hasCampaignContext = (attribution: MarketingAttribution): boolean =>
  attribution.capturedAt !== undefined ||
  attribution.fbc !== undefined ||
  attribution.fbclid !== undefined ||
  attribution.fbp !== undefined ||
  attribution.journeyId !== undefined ||
  attribution.landingPath !== undefined ||
  attribution.referrerDomain !== undefined ||
  Object.keys(attribution.utmParams).length > 0;

const persistLocalAttribution = (attribution: MarketingAttribution): void => {
  try {
    localStorage.setItem(MARKETING_ATTRIBUTION_LOCAL_STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Local storage can be unavailable in privacy-restricted contexts.
  }
};

/**
 * Merges a validated browser-to-desktop handoff into the renderer's durable copy.
 *
 * The handoff can arrive before or after the renderer starts. Keeping this
 * operation idempotent lets the initial IPC read and live update race safely.
 */
export const captureMarketingAttribution = ({
  attribution,
}: {
  attribution: MarketingAttribution;
}): void => {
  if (attribution.capturedAt === undefined || isExpiredAttribution(attribution)) {
    return;
  }

  const mergedAttribution = mergeAttribution({
    incoming: attribution,
    persisted: latestPersistedAttribution(),
  });

  if (mergedAttribution === null) {
    return;
  }

  capturedMarketingAttribution = mergedAttribution;
  persistLocalAttribution(mergedAttribution);
};

const stripMarketingAttributionFromSearch = (searchParams: URLSearchParams): string => {
  const newParams = new URLSearchParams(searchParams);
  for (const key of Array.from(newParams.keys())) {
    if (isMarketingAttributionQueryParam(key)) {
      newParams.delete(key);
    }
  }

  const result = newParams.toString();
  return result.length > 0 ? `?${result}` : "";
};

/**
 * Captures the shared Market attribution contract before the router initializes.
 *
 * Browser routes receive it directly from Market links or the shared `.mem.ai`
 * cookie. Desktop routes receive the same fields through the OAuth deep link.
 * The local record keeps delayed web and desktop conversions attributable for
 * seven days without extending the original campaign capture window.
 */
export const captureAndStripMarketingAttribution = (): void => {
  const currentUrl = new URL(document.URL);

  let currentPath: string;
  let searchParams: URLSearchParams;
  if (clientEnvModule.isDesktop()) {
    const hash = currentUrl.hash || "#/";
    const hashParts = hash.split("?");
    currentPath = hashParts[0] || "#/";
    searchParams = new URLSearchParams(hashParts[1] || "");
  } else {
    currentPath = currentUrl.pathname;
    searchParams = new URLSearchParams(currentUrl.search);
  }

  const rawIncoming = extractQueryAttribution(searchParams);
  const shouldTimestampIncoming = rawIncoming !== null && hasCampaignContext(rawIncoming);
  const incoming = (() => {
    if (rawIncoming === null) {
      return null;
    }

    return {
      ...rawIncoming,
      capturedAt:
        rawIncoming.capturedAt ?? (shouldTimestampIncoming ? new Date().toISOString() : undefined),
    };
  })();
  const activeIncoming =
    incoming?.capturedAt !== undefined && isExpiredAttribution(incoming) ? null : incoming;
  capturedMarketingAttribution = mergeAttribution({
    incoming: activeIncoming,
    persisted: latestPersistedAttribution(),
  });

  if (capturedMarketingAttribution?.capturedAt !== undefined) {
    persistLocalAttribution(capturedMarketingAttribution);
  }

  if (rawIncoming === null) {
    return;
  }

  const newSearch = stripMarketingAttributionFromSearch(searchParams);
  window.history.replaceState(null, "", currentPath + newSearch);
};

export const getCapturedMarketingAttribution = (): MarketingAttribution | null => {
  if (
    capturedMarketingAttribution?.capturedAt !== undefined &&
    isExpiredAttribution(capturedMarketingAttribution)
  ) {
    capturedMarketingAttribution = null;
    removeLocalAttribution();
  }

  capturedMarketingAttribution ??= latestPersistedAttribution();
  return capturedMarketingAttribution;
};

export const clearCapturedMarketingAttribution = (): void => {
  capturedMarketingAttribution = null;
  removeLocalAttribution();
};

export const appendMarketingAttributionToSearchParams = ({
  attribution = getCapturedMarketingAttribution(),
  searchParams,
}: {
  attribution?: MarketingAttribution | null;
  searchParams: URLSearchParams;
}): void => {
  if (attribution === null) {
    return;
  }

  for (const [key, value] of Object.entries(attribution.utmParams)) {
    searchParams.set(key, value);
  }

  const params = {
    [MarketingAttributionQueryParam.CAPTURED_AT]: attribution.capturedAt,
    [MarketingAttributionQueryParam.FBC]: attribution.fbc,
    [MarketingAttributionQueryParam.FBCLID]: attribution.fbclid,
    [MarketingAttributionQueryParam.FBP]: attribution.fbp,
    [MarketingAttributionQueryParam.JOURNEY_ID]: attribution.journeyId,
    [MarketingAttributionQueryParam.LANDING_PATH]: attribution.landingPath,
    [MarketingAttributionQueryParam.REFERRER_DOMAIN]: attribution.referrerDomain,
    [MarketingAttributionQueryParam.SEGMENT_ANONYMOUS_ID]: attribution.segmentAnonymousId,
  };
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      searchParams.set(key, value);
    }
  }
};

export const decorateMarketingAttributionUrl = ({
  attribution = getCapturedMarketingAttribution(),
  url,
}: {
  attribution?: MarketingAttribution | null;
  url: URL;
}): URL => {
  const decoratedUrl = new URL(url.toString());
  appendMarketingAttributionToSearchParams({
    attribution,
    searchParams: decoratedUrl.searchParams,
  });
  return decoratedUrl;
};

export const buildMetaFbc = ({
  capturedAt,
  fbclid,
}: {
  capturedAt?: string;
  fbclid?: string;
}): string | undefined => {
  if (fbclid === undefined) {
    return undefined;
  }

  const parsedTimestamp = capturedAt === undefined ? Number.NaN : Date.parse(capturedAt);
  const timestamp = Number.isFinite(parsedTimestamp) ? Math.floor(parsedTimestamp) : Date.now();
  return `fb.1.${timestamp}.${fbclid}`;
};
